import { Link } from "react-router-dom";
import { useConsent } from "@/contexts/ConsentContext";

export default function Privacy() {
    const { openPreferences } = useConsent();
    return (
        <div className="ok-container py-12 max-w-3xl mx-auto" data-testid="privacy-page">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Privacy</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-2">Privacy policy</h1>
            <p className="text-xs opacity-60 mt-1">Last updated: 13 June 2026</p>

            <div className="prose prose-invert mt-8 text-sm leading-relaxed space-y-6 max-w-none">
                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Who we are</h2>
                    <p>
                        Project UNVEILED ("we", "us", "the archive") operates the website at projectunveiled.com
                        and the associated public archive of UFO, UAP, NHI and anomalous encounters.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">What we collect</h2>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><b>Account data</b> — your email address, display name, password hash, optional avatar / bio / location.</li>
                        <li><b>Content you submit</b> — encounter reports, comments, posts, media uploads, votes, follows.</li>
                        <li><b>Payment data</b> — transactions are handled by Stripe. We store only the transaction ID, plan, amount, currency and subscription status. We never see or store card numbers.</li>
                        <li><b>Technical data</b> — IP address, browser type, device, pages visited. Used for security and aggregate analytics only.</li>
                        <li><b>Analytics &amp; advertising measurement</b> — only if you consent, see "Tracking technologies" below.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Why we use it</h2>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Operate the archive: account login, comments, submissions, search, messaging.</li>
                        <li>Moderate content and protect the community.</li>
                        <li>Process subscription payments (Stripe).</li>
                        <li>Send transactional emails (password resets, payment confirmations) via Resend.</li>
                        <li>Measure how the archive is used — only with your consent.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Tracking technologies</h2>
                    <p>
                        We use the following third-party tracking technologies on the site. All of them are
                        <b> denied by default</b> until you provide explicit consent through the cookie banner or preferences.
                    </p>
                    <ul className="list-disc pl-5 space-y-2 mt-2">
                        <li>
                            <b>Google Analytics 4 (GA4)</b> — measures page views, navigation and conversion events.
                            Runs under Google Consent Mode v2: storage is "denied" by default, and only activated when you
                            grant analytics consent. Google's privacy policy:{" "}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">policies.google.com/privacy</a>.
                        </li>
                        <li>
                            <b>Meta Pixel</b> (also known as Facebook Pixel) — used to measure the performance of campaigns we run on Meta
                            properties (Facebook, Instagram). Only loaded after you grant advertising consent. Meta privacy policy:{" "}
                            <a href="https://www.facebook.com/policy.php" target="_blank" rel="noopener noreferrer" className="underline">facebook.com/policy.php</a>.
                        </li>
                        <li>
                            <b>TikTok Pixel</b> — used to measure the performance of TikTok ad campaigns. Only loaded
                            after you grant advertising consent. TikTok privacy policy:{" "}
                            <a href="https://www.tiktok.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">tiktok.com/legal/privacy-policy</a>.
                        </li>
                    </ul>
                    <p className="mt-3">
                        We never send the following to any tracker: your password, your raw email address, your encounter
                        description text, payment card data, or any sensitive personal data.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Your choices</h2>
                    <p>You can:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Change your consent preferences at any time via the{" "}
                            <button onClick={openPreferences} className="underline hover:text-[var(--ok-accent)]" data-testid="privacy-open-prefs">manage cookies</button>{" "}link.</li>
                        <li>Edit or delete your account from your profile page.</li>
                        <li>Request a copy or deletion of your data by emailing us via the <Link to="/contact" className="underline">contact form</Link>.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Data retention</h2>
                    <p>
                        Account and submission data are kept while your account is active. If you delete your account, we
                        delete your personal account data within 30 days. Anonymised aggregate analytics may be retained
                        for longer for historical reporting. Payment records are kept for the period required by tax law (typically 7 years).
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Sharing</h2>
                    <p>
                        We don't sell personal data. We share the minimum necessary data with our payment processor (Stripe)
                        and email provider (Resend) to deliver the service. Public content you post (encounters, comments, posts,
                        public profile) is visible to all visitors by design.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Contact</h2>
                    <p>
                        For any privacy questions, use the <Link to="/contact" className="underline">contact form</Link>.
                    </p>
                </section>
            </div>
        </div>
    );
}
